import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  createCustomsInvoice,
  createMsr,
  fetchCustomsInvoice,
  fetchHandlingUnits,
  fetchLocation,
  fetchMsr,
  fetchMsrExceptions,
  fetchMsrNumber,
  fetchMsrOptions,
  fetchPickAndPack,
  saveMsrAttributes,
  saveMsrParts,
  recheckMsrExceptions,
  resendToTms,
  savePickAndPack,
  submitMsr,
} from "../services/msrApi";
import {
  locationFromApiRecord,
  normalizeLocation,
} from "../utils/msrLocations";
import { newMsrLocationDefaults } from "../utils/msrDefaults";
import {
  headerToApi,
  pickPackFromApi,
  pickPackPayload,
  toCreatePayload,
  totalValueOf,
} from "../utils/msrPayload";
import {
  HEADER_SECTIONS,
  isHeaderSection,
  PARTS_EXTRAS_KEY,
  PICK_PACK_KEYS,
  PICK_PACK_SAVE_KEYS,
  RETIRED_SECTIONS,
  sidebarKey,
  stageOf,
  STAGE_PICK_PACK,
} from "../utils/msrSections";
import { errorSections } from "../utils/msrValidation";
import { isMsrLockedError } from "../utils/msrLock";
import {
  markerKeyOf,
  problemsFromServer,
  saveErrorMessage,
  saveFailureKind,
  isServerFailure,
} from "../utils/msrSaveProblems";

// LWI-1549 — the MSR wizard store. Modelled on the CCI wizard's useInvoiceStore:
// the whole record lives here, each section page saves through saveSection(),
// completedSteps / saveErrorSteps drive the sidebar markers (LWI-1219 pattern).

/** Create-page defaults (LWI-1402 USD, LWI-1461 CI, prepaid; CA Charge To). */
export const NEW_MSR_DETAILS = {
  msrNumber: "",
  requestedBy: "",
  prepaidOrCollect: "prepaid",
  isHot: 0,
  reasonForShipment: "",
  msrContainsWipJob: "",
  governmentProperty: false,
  authorizedName: "",
  areasOfOperation: "",
  shipmentType: "", // LWI-1594 — optional, no default
  cmrNumber: "",
  ciNumber: "",
  sinNumber: "",
  vcrNumber: "",
  receiptNumber: "",
  originalPO: "",
  vendorRep: "",
  customerTaxLICFed: "",
  customerTaxLICProv: "",
  endUse: "",
  otherReference: "",
  notes: "",
  chargeType: "",
  currency: "USD",
  customsValue: "",
  transportationAccount: "",
  project: "",
  program: "",
  incoTerms: "",
  namedPlace: "",
  shipmentPriority: "",
  owner: "General Dynamics Land Systems - US",
  shipFrom: null,
  shipTo: null,
  chargeTo: normalizeLocation({
    locationCode: "HCLLLO03",
    name: "HCL OXFORD",
    address1: "2021 Oxford ST. E.",
    address2: "",
    city: "LONDON",
    state: "ON",
    zip: "N5V2Z7",
    country: "CANADA",
    open: "06:30",
    close: "21:45",
    contactName: "IAN",
    contactPhone: "5196814254",
    contactEmail: "3plshipping@gdls.com",
  }),
};

export const EMPTY_OPTIONS = {
  incoTerms: [],
  currencies: [],
  chargeTypes: [],
  reasons: [],
  priorities: [],
  projects: [],
  programs: [],
  shipVia: [],
  shippingMethods: [],
  authorizedNames: [],
};

// LWI-1644 — bumped by every exceptions load and row merge; a load whose
// number has moved on by the time it lands is dropped, so an older response
// never overwrites a newer list or a just-merged resolve / reopen row.
let exceptionsSeq = 0;

const initialState = {
  msrNumber: null,
  shipmentId: null,
  status: null,
  isDraft: true,
  cancelledAt: null,
  // LWI-1732 - the TMS has the shipment (server rule on the synced columns).
  tmsAccepted: false,
  // LWI-1655 - sent to the TMS: content is locked (never for a cancelled MSR).
  locked: false,
  lockedAt: null,
  lockedBy: null,
  // True between startNew() and the one-time tenant/profile defaults pass.
  needsNewDefaults: false,
  details: { ...NEW_MSR_DETAILS },
  parts: [],
  pickAndPack: pickPackFromApi({}),
  handlingUnits: [],
  customsInvoice: null,
  hasCustomsInvoice: false,
  options: EMPTY_OPTIONS,
  optionsLoaded: false,
  isLoading: false,
  loadError: null,
  isSaving: false,
  completedSteps: [],
  saveErrorSteps: [],
  // LWI-1697 - the 422 `problems` per marker key, and the markers whose last
  // save failed on the server (5xx / no response): no highlights, only copy.
  saveProblems: {},
  serverFailedSteps: [],
  dirtySections: [],
  // Bumped by every edit; a save compares it before and after its request.
  editSeq: 0,
  // LWI-1685 - bumped by every successful Pick and Pack save (the Bill of Lading
  // view re-reads the TMS push log on it); isSendingTms guards double sends.
  pickPackSaveSeq: 0,
  isSendingTms: false,
  // LWI-1644 - one shared read of msr/exceptions/{id} for the header chip,
  // the Parts grid, the line drawer and the Exceptions tab.
  exceptions: { rows: null, status: "idle", error: null },
};

/**
 * Server 422 → section marker keys. Server keys are wizard sections, except
 * the retired References & Notes / Charges & Costs (the server still files
 * Currency under "charges"): their fields now save as the Parts tab's
 * header part (LWI-1604), so they mark that key and a header save clears them.
 */
export const markersFromServerErrors = (data) => {
  const errors = data?.errors;
  const fromErrors =
    errors && typeof errors === "object"
      ? errorSections(errors).map((k) =>
          RETIRED_SECTIONS[k] ? PARTS_EXTRAS_KEY : k
        )
      : [];
  // LWI-1697 - the structured problems name their sections too.
  const fromProblems = problemsFromServer(data).map((p) =>
    markerKeyOf(p.section)
  );
  return [...new Set([...fromErrors, ...fromProblems.filter(Boolean)])];
};

/** LWI-1697 - the problems of a 422, grouped by marker key. */
const problemsByMarker = (data) =>
  problemsFromServer(data).reduce((acc, p) => {
    const k = markerKeyOf(p.section);
    if (!k) return acc;
    acc[k] = [...(acc[k] || []), p];
    return acc;
  }, {});

/** LWI-1604 — which part of the Parts tab failed to save, for the message. */
export const PARTS_TAB_PART_LABELS = {
  parts: "Parts",
  [PARTS_EXTRAS_KEY]: "References & Notes",
};

/** LWI-1596 — which half of the Shipment and Parts tab failed to save. */
export const SHIPMENT_TAB_PART_LABELS = {
  essentials: "Shipment details",
  parts: "Parts",
};

const without = (list, keys) => list.filter((k) => !keys.includes(k));

export const useMsrWizardStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      reset: () =>
        set({
          ...initialState,
          details: { ...NEW_MSR_DETAILS },
          pickAndPack: pickPackFromApi({}),
        }),

      // ---------------------------------------------------------- exceptions

      /**
       * Reload the MSR's exception rows. Keeps the last rows while reloading.
       * Resolves true when loaded, false when the load failed, null when a
       * newer load or an MSR switch superseded it.
       */
      loadExceptions: async () => {
        const id = get().shipmentId;
        if (!id) return null;
        const seq = ++exceptionsSeq;
        set((s) => ({ exceptions: { ...s.exceptions, status: "loading", error: null } }));
        try {
          const rows = await fetchMsrExceptions(id);
          if (seq !== exceptionsSeq || get().shipmentId !== id) return null;
          set({ exceptions: { rows, status: "ready", error: null } });
          return true;
        } catch (error) {
          if (seq !== exceptionsSeq || get().shipmentId !== id) return null;
          set((s) => ({ exceptions: { rows: s.exceptions.rows, status: "error", error } }));
          return false;
        }
      },

      /** Merge a row returned by resolve / reopen into the shared list. */
      replaceException: (row) => {
        // A load already in flight predates this change: drop it, and reload
        // so the rest of the list is current too.
        const wasLoading = get().exceptions.status === "loading";
        exceptionsSeq += 1;
        set((s) => ({
          exceptions: {
            ...s.exceptions,
            status: wasLoading ? "ready" : s.exceptions.status,
            rows: (s.exceptions.rows || []).map((r) =>
              r.id === row.id ? { ...r, ...row } : r
            ),
          },
        }));
        if (wasLoading) get().loadExceptions();
      },

      /**
       * LWI-1756 - the check runs on the server against the stored lines, so
       * unsaved part edits are saved first (the parts section only: the rules
       * read lines, and a Save Progress of sibling sections is the user's
       * call). A failed save throws with `partsSaveFailed` set and no check is
       * made; saveSection has already filed the error and its problems.
       * POST the check, then reload the rows. Returns the server's response.
       */
      recheckExceptions: async () => {
        const id = get().shipmentId;
        if (get().dirtySections.includes("parts")) {
          try {
            await get().saveSection("parts");
          } catch (error) {
            if (error && typeof error === "object") error.partsSaveFailed = true;
            throw error;
          }
        }
        const data = await recheckMsrExceptions(id);
        if ((await get().loadExceptions()) === false) {
          throw new Error("The check ran, but the list could not be refreshed.");
        }
        return data;
      },

      // ------------------------------------------------------------ loading

      loadOptions: async () => {
        if (get().optionsLoaded) return;
        const options = await fetchMsrOptions();
        set({ options, optionsLoaded: true });
      },

      /** A brand-new MSR: reserve a number, seed the Create-page defaults. */
      startNew: async () => {
        // Reset synchronously: the store is global, so it may still hold the
        // last MSR the user viewed. Clearing msrNumber before the async number
        // fetch is what stops the "new" route bouncing to that stale MSR.
        set({
          ...initialState,
          options: get().options,
          optionsLoaded: get().optionsLoaded,
          details: { ...NEW_MSR_DETAILS },
          pickAndPack: pickPackFromApi({}),
          isLoading: true,
        });
        try {
          const number = await fetchMsrNumber();
          set({
            ...initialState,
            options: get().options,
            optionsLoaded: get().optionsLoaded,
            details: { ...NEW_MSR_DETAILS, msrNumber: number },
            pickAndPack: pickPackFromApi({}),
            isLoading: false,
            needsNewDefaults: true,
          });
          return number;
        } catch (error) {
          set({ isLoading: false, loadError: error });
          throw error;
        }
      },

      /**
       * One-time location defaults for a fresh MSR (LWI-1143 profile Ship
       * From, LWI-1378 US Sterling Heights). Called by the page once the auth
       * user is known; a no-op after the first pass or on a loaded MSR.
       */
      applyNewMsrDefaults: async ({ tenant, defaultLocationCode } = {}) => {
        if (!get().needsNewDefaults) return;
        set({ needsNewDefaults: false });
        let profileShipFrom = null;
        if (defaultLocationCode) {
          try {
            profileShipFrom = locationFromApiRecord(
              await fetchLocation(defaultLocationCode),
              defaultLocationCode
            );
          } catch {
            profileShipFrom = null; // fall through to the tenant default
          }
        }
        const patch = newMsrLocationDefaults({ tenant, profileShipFrom });
        if (Object.keys(patch).length) {
          set((state) => ({ details: { ...state.details, ...patch } }));
        }
      },

      /** LWI-1143 — the user picked another of their profile default locations for Ship From. */
      applyProfileShipFrom: async (code) => {
        if (!code) return;
        const loc = locationFromApiRecord(await fetchLocation(code), code);
        if (loc) get().updateLocation("shipFrom", loc);
      },

      /** Load an existing MSR (plus Pick and Pack + handling units when it has a shipment id). */
      loadMsr: async (msrNumber) => {
        set({ isLoading: true, loadError: null });
        try {
          const data = await fetchMsr(msrNumber);
          if (!data) throw new Error(`${msrNumber} not found`);
          const { parts = [], ...header } = data;
          const details = {
            ...NEW_MSR_DETAILS,
            ...header,
            shipFrom: normalizeLocation(header.shipFrom),
            shipTo: normalizeLocation(header.shipTo),
            chargeTo: normalizeLocation(header.chargeTo),
          };
          const patch = {
            msrNumber: data.msrNumber,
            shipmentId: data.id,
            status: data.status,
            isDraft: !!data.isDraft,
            cancelledAt: data.cancelledAt ?? null,
            tmsAccepted: !!data.tmsAccepted,
            locked: !!data.locked,
            lockedAt: data.lockedAt ?? null,
            lockedBy: data.lockedBy ?? null,
            needsNewDefaults: false,
            details,
            parts: Array.isArray(parts) ? parts : [],
            completedSteps: [],
            saveErrorSteps: [],
            saveProblems: {},
            serverFailedSteps: [],
            dirtySections: [],
          };
          // LWI-1644 - another MSR starts with no exception rows.
          if (get().shipmentId !== data.id) {
            patch.exceptions = { rows: null, status: "idle", error: null };
          }
          if (data.id) {
            const [pp, hu, customs] = await Promise.allSettled([
              fetchPickAndPack(data.id),
              fetchHandlingUnits(data.id),
              fetchCustomsInvoice(data.id),
            ]);
            patch.pickAndPack = pickPackFromApi(
              pp.status === "fulfilled" && pp.value ? pp.value : {}
            );
            patch.handlingUnits =
              hu.status === "fulfilled" && Array.isArray(hu.value)
                ? hu.value
                : [];
            patch.customsInvoice =
              customs.status === "fulfilled" && customs.value
                ? customs.value
                : null;
            patch.hasCustomsInvoice = !!patch.customsInvoice;
          }
          set({ ...patch, isLoading: false });
          return data;
        } catch (error) {
          set({ isLoading: false, loadError: error });
          throw error;
        }
      },

      // ------------------------------------------------------------ editing

      updateDetails: (patch, section) =>
        set((state) => ({
          editSeq: state.editSeq + 1,
          details: { ...state.details, ...patch },
          dirtySections:
            section && !state.dirtySections.includes(section)
              ? [...state.dirtySections, section]
              : state.dirtySections,
        })),

      /** LocationCard.onChange → normalised block; also keeps the derived Ship From/To state fresh. */
      updateLocation: (type, raw) =>
        set((state) => ({
          editSeq: state.editSeq + 1,
          details: { ...state.details, [type]: normalizeLocation(raw) },
          dirtySections: state.dirtySections.includes("essentials")
            ? state.dirtySections
            : [...state.dirtySections, "essentials"],
        })),

      /** PartsTable contract: setParts(arrayOrUpdater). Total Value follows the lines (LWI-1460). */
      setParts: (next) =>
        set((state) => {
          const parts = typeof next === "function" ? next(state.parts) : next;
          const list = Array.isArray(parts) ? parts : [];
          return {
            editSeq: state.editSeq + 1,
            parts: list,
            details: { ...state.details, customsValue: totalValueOf(list) },
            dirtySections: state.dirtySections.includes("parts")
              ? state.dirtySections
              : [...state.dirtySections, "parts"],
          };
        }),

      updatePickAndPack: (patch, section = "pp-customer-info") =>
        set((state) => ({
          editSeq: state.editSeq + 1,
          pickAndPack: { ...state.pickAndPack, ...patch },
          dirtySections: state.dirtySections.includes(section)
            ? state.dirtySections
            : [...state.dirtySections, section],
        })),

      setHandlingUnits: (next) =>
        set((state) => ({
          editSeq: state.editSeq + 1,
          handlingUnits:
            typeof next === "function" ? next(state.handlingUnits) : next,
          dirtySections: state.dirtySections.includes("pp-handling-units")
            ? state.dirtySections
            : [...state.dirtySections, "pp-handling-units"],
        })),

      markStepComplete: (key) =>
        set((state) => ({
          completedSteps: state.completedSteps.includes(key)
            ? state.completedSteps
            : [...state.completedSteps, key],
        })),

      // ------------------------------------------------------------- saving

      /**
       * Save one section. Header tabs share PUT /attributes (the whole header
       * is sent so unsaved edits on sibling tabs are never lost), Parts has its
       * own endpoint, Pick and Pack tabs share the existing pickandpack PUT. A
       * brand-new MSR is created as a draft on its first save.
       */
      saveSection: async (section) => {
        set({ isSaving: true });
        try {
          const state = get();
          let response;
          let createdHere = false;
          if (!state.msrNumber) {
            createdHere = true;
            const number = state.details.msrNumber || (await fetchMsrNumber());
            response = await createMsr(
              number,
              toCreatePayload(number, state.details, state.parts, {
                isDraft: true,
              })
            );
            const finalNumber = response?.msr_number || number;
            const loaded = await fetchMsr(finalNumber);
            // LWI-1628 — patch the LIVE details: `state` is the snapshot from
            // before the create request, and writing it back silently undid any
            // edit (a Ship To picked while the save was in flight) made since.
            set((s) => ({
              msrNumber: finalNumber,
              shipmentId: loaded?.id ?? null,
              status: loaded?.status ?? 1,
              isDraft: true,
              details: { ...s.details, msrNumber: finalNumber },
            }));
          } else if (isHeaderSection(section)) {
            response = await saveMsrAttributes(
              state.msrNumber,
              headerToApi(state.details)
            );
          } else if (section === "parts") {
            // LWI-1696 — the part list of an MSR that is still loading, or failed
            // to load, is empty or stale; saving it would remove stored lines.
            if (state.isLoading || state.loadError)
              throw new Error(
                "The MSR has not finished loading. Reload the page, then save again."
              );
            response = await saveMsrParts(state.msrNumber, state.parts);
          } else if (stageOf(sidebarKey(section)) === STAGE_PICK_PACK) {
            if (!state.shipmentId)
              throw new Error("Save the MSR before saving Pick and Pack.");
            // Keyed by the MSR number: the status change, audit and TMS export
            // behind this endpoint look the MSR up by shipment_number.
            response = await savePickAndPack(
              state.msrNumber,
              pickPackPayload(state)
            );
          } else {
            throw new Error(`Unknown section: ${section}`);
          }
          // LWI-1596 — the create carried the part lines too, so they are saved.
          const cleared = createdHere
            ? [...HEADER_SECTIONS, "parts"]
            : isHeaderSection(section)
            ? HEADER_SECTIONS
            : stageOf(sidebarKey(section)) === STAGE_PICK_PACK
            ? PICK_PACK_SAVE_KEYS
            : [section];
          // …and count as completed when there were any to save.
          const alsoCompleted = createdHere && state.parts.length > 0 ? ["parts"] : [];
          set((s) => ({
            saveErrorSteps: without(s.saveErrorSteps, cleared),
            serverFailedSteps: without(s.serverFailedSteps, cleared),
            saveProblems: Object.fromEntries(
              Object.entries(s.saveProblems).filter(([k]) => !cleared.includes(k))
            ),
            // LWI-1628 — an edit made while the request was in flight was not
            // in the payload, so its section must stay dirty (one redundant
            // save beats a lost Ship To).
            dirtySections:
              s.editSeq !== state.editSeq
                ? s.dirtySections
                : without(s.dirtySections, cleared),
            // The Parts tab's optional fields never count toward its completed state.
            completedSteps:
              section === PARTS_EXTRAS_KEY
                ? s.completedSteps
                : [...new Set([...s.completedSteps, section, ...alsoCompleted])],
          }));
          if (stageOf(sidebarKey(section)) === STAGE_PICK_PACK)
            set((s) => ({ pickPackSaveSeq: s.pickPackSaveSeq + 1 }));
          return response;
        } catch (error) {
          const kind = saveFailureKind(error);
          set((s) => {
            const markers =
              kind === "validation"
                ? markersFromServerErrors(error.response.data)
                : kind === "locked"
                ? [] // the lock is not a field problem
                : [section];
            const grouped =
              kind === "validation" ? problemsByMarker(error.response.data) : {};
            const saveProblems = { ...s.saveProblems };
            // A retry replaces what the marker held, so lists never pile up.
            markers.forEach((k) => {
              if (grouped[k]) saveProblems[k] = grouped[k];
              else delete saveProblems[k];
            });
            return {
              saveErrorSteps: [...new Set([...s.saveErrorSteps, ...markers])],
              saveProblems,
              serverFailedSteps:
                kind === "server"
                  ? [...new Set([...s.serverFailedSteps, section])]
                  : without(s.serverFailedSteps, markers),
            };
          });
          throw error;
        } finally {
          set({ isSaving: false });
        }
      },

      /**
       * LWI-1596 — Save Progress on the Shipment and Parts tab: the header
       * (PUT /attributes, which carries the essentials and the parts-refs
       * fields together) and the part lines (PUT /parts), each only when it
       * has unsaved changes; with nothing unsaved both are saved. One failing
       * does not stop the other, and the error names the half that failed. A
       * new MSR is created by one save that carries both.
       */
      saveShipmentTab: async () => {
        const { msrNumber, dirtySections } = get();
        const save = get().saveSection;
        if (!msrNumber) return save("essentials");
        const wanted = [];
        if (dirtySections.some((k) => k === "essentials" || k === PARTS_EXTRAS_KEY))
          wanted.push("essentials");
        if (dirtySections.includes("parts")) wanted.push("parts");
        // LWI-1696 — with nothing unsaved, re-send the lines only when there
        // are some: an empty list is never "save no parts".
        if (!wanted.length)
          wanted.push(...(get().parts.length ? ["essentials", "parts"] : ["essentials"]));
        const failed = [];
        for (const key of wanted) {
          try {
            await save(key);
          } catch (error) {
            failed.push({ key, error });
          }
        }
        if (!failed.length) return true;
        // LWI-1655 - keep the original error so the page sees msr_locked.
        const lockedFailure = failed.find((f) => isMsrLockedError(f.error));
        if (lockedFailure) throw lockedFailure.error;
        const err = new Error(
          failed
            .map(({ key, error }) =>
              `${SHIPMENT_TAB_PART_LABELS[key]} did not save: ${saveErrorMessage(
                error,
                "please try again"
              )}`
            )
            .join(" ")
        );
        err.failedParts = failed.map((f) => f.key);
        err.failures = failed;
        err.isServerFailure = isServerFailure(err);
        throw err;
      },

      /**
       * LWI-1604 — Save Progress on the Parts tab: the part lines (PUT /parts)
       * and its optional header fields (PUT /attributes), each only when it
       * has unsaved changes. One failing does not stop the other; the error
       * names the part that failed and its edits stay in the store for a retry.
       * With nothing unsaved it saves the lines, as before. A new MSR is
       * created by one save that carries both.
       */
      savePartsTab: async () => {
        const { msrNumber, dirtySections } = get();
        const save = get().saveSection;
        if (!msrNumber) return save("parts");
        const wanted = ["parts", PARTS_EXTRAS_KEY].filter((k) =>
          dirtySections.includes(k)
        );
        // LWI-1696 — as on the Shipment and Parts tab: with nothing unsaved,
        // an empty list is not re-sent.
        if (!wanted.length) {
          if (!get().parts.length) return true;
          wanted.push("parts");
        }
        const failed = [];
        for (const key of wanted) {
          try {
            await save(key);
          } catch (error) {
            failed.push({ key, error });
          }
        }
        if (!failed.length) return true;
        // LWI-1655 - keep the original error so the page sees msr_locked.
        const lockedFailure = failed.find((f) => isMsrLockedError(f.error));
        if (lockedFailure) throw lockedFailure.error;
        const err = new Error(
          failed
            .map(({ key, error }) =>
              `${PARTS_TAB_PART_LABELS[key]} did not save: ${saveErrorMessage(
                error,
                "please try again"
              )}`
            )
            .join(" ")
        );
        err.failedParts = failed.map((f) => f.key);
        err.failures = failed;
        err.isServerFailure = isServerFailure(err);
        throw err;
      },

      /** Draft → Pick and Pack. 422 marks the sections the server named. */
      submitMsr: async () => {
        set({ isSaving: true });
        try {
          const { msrNumber } = get();
          if (!msrNumber) throw new Error("Save the MSR before submitting it.");
          const response = await submitMsr(msrNumber);
          set({
            status: response?.status ?? 2,
            isDraft: false,
            saveErrorSteps: [],
            saveProblems: {},
            serverFailedSteps: [],
          });
          return response;
        } catch (error) {
          const kind = saveFailureKind(error);
          if (kind === "validation") {
            set({
              saveErrorSteps: markersFromServerErrors(error.response.data),
              saveProblems: problemsByMarker(error.response.data),
              serverFailedSteps: [],
            });
          } else if (kind === "server") {
            // Nothing was submitted: mark the draft tab with the server copy.
            set((s) => ({
              serverFailedSteps: [...new Set([...s.serverFailedSteps, "essentials"])],
              saveErrorSteps: [...new Set([...s.saveErrorSteps, "essentials"])],
            }));
          }
          throw error;
        } finally {
          set({ isSaving: false });
        }
      },

      /**
       * LWI-1685 - Send to TMS on the Bill of Lading view: save any unsaved
       * Pick and Pack changes first (the resend reads saved units only), then
       * resend. A failed save sends nothing; its error is tagged phase "save".
       * A second call while one is running is ignored ({ skipped: true }).
       */
      savePickPackThenResend: async () => {
        if (get().isSaving || get().isSendingTms) return { skipped: true };
        set({ isSendingTms: true });
        try {
          const { dirtySections } = get();
          const dirtyKey = PICK_PACK_SAVE_KEYS.find((k) =>
            dirtySections.includes(k)
          );
          if (dirtyKey) {
            try {
              await get().saveSection(dirtyKey);
            } catch (error) {
              if (error && typeof error === "object") error.phase = "save";
              throw error;
            }
          }
          const result = await resendToTms(get().msrNumber);
          // An edit made while the save or send was in flight was not sent.
          const unsaved = PICK_PACK_SAVE_KEYS.some((k) =>
            get().dirtySections.includes(k)
          );
          return { saved: !!dirtyKey, unsaved, result };
        } finally {
          set({ isSendingTms: false });
        }
      },

      /** Pick and Pack → Bill of Lading (status 3). The server refuses (409) while exceptions are open. */
      submitPickAndPack: async () => {
        set({ isSaving: true });
        try {
          const state = get();
          if (!state.shipmentId)
            throw new Error("Save the MSR before submitting Pick and Pack.");
          const response = await savePickAndPack(
            state.msrNumber,
            pickPackPayload(state, { status: 3 })
          );
          set({
            status: 3,
            dirtySections: without(state.dirtySections, PICK_PACK_SAVE_KEYS),
            pickPackSaveSeq: get().pickPackSaveSeq + 1,
          });
          return response;
        } finally {
          set({ isSaving: false });
        }
      },

      createCustomsInvoice: async () => {
        const { shipmentId } = get();
        if (!shipmentId)
          throw new Error("Save the MSR before creating a customs invoice.");
        const invoice = await createCustomsInvoice(shipmentId);
        set({ customsInvoice: invoice, hasCustomsInvoice: !!invoice });
        return invoice;
      },
    }),
    { name: "msr-wizard" }
  )
);

export default useMsrWizardStore;
